import { get, post, put, patch, del, updateServerTimeOffset } from '@aiptyg/api-client';
import { buildConfig } from '@aiptyg/config';
import { getAccessToken, handleUnauthorized } from '../../../store/auth.store';
import type {
  TaskListResponseDTO,
  TaskDTO,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskStatus,
  DownloadAppInfoDTO,
  MessageImportBatchDTO,
  MessagePoolStatsDTO,
} from '../types/tasks.types';

export function listTasks(params: {
  source?: string;
  agentId?: string;
  page: number;
  pageSize: number;
  status: string;
  marketId?: string;
  category: string;
  taskType: string;
  platform: string;
}): Promise<TaskListResponseDTO> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  if (params.source) qs.set('source', params.source);
  if (params.agentId) qs.set('agent_id', params.agentId);
  qs.set('page_size', String(params.pageSize));
  if (params.status) qs.set('status', params.status);
  if (params.marketId) qs.set('market_id', params.marketId);
  if (params.category) qs.set('category', params.category);
  if (params.taskType) qs.set('task_type', params.taskType);
  if (params.platform) qs.set('platform', params.platform);
  return get<TaskListResponseDTO>(`/v1/admin/tasks?${qs.toString()}`);
}

export function getTask(id: number): Promise<TaskDTO> {
  return get<TaskDTO>(`/v1/admin/tasks/${id}`);
}

export function createTask(data: CreateTaskRequest): Promise<TaskDTO> {
  const body: Record<string, unknown> = { ...data };
  return post<TaskDTO>('/v1/admin/tasks', body);
}

export function updateTask(id: number, data: UpdateTaskRequest): Promise<TaskDTO> {
  const body: Record<string, unknown> = { ...data };
  return put<TaskDTO>(`/v1/admin/tasks/${id}`, body);
}

export function deleteTask(id: number): Promise<void> {
  return del<void>(`/v1/admin/tasks/${id}`);
}

export function changeTaskStatus(id: number, status: TaskStatus): Promise<TaskDTO> {
  return patch<TaskDTO>(`/v1/admin/tasks/${id}/status`, { status });
}

export function appStoreLookup(params: {
  bundleId?: string;
  appId?: number;
  country?: string;
}): Promise<DownloadAppInfoDTO> {
  const qs = new URLSearchParams();
  if (params.bundleId) qs.set('bundle_id', params.bundleId);
  if (params.appId) qs.set('app_id', String(params.appId));
  if (params.country) qs.set('country', params.country);
  return get<DownloadAppInfoDTO>(`/v1/admin/tasks/app-store-lookup?${qs.toString()}`);
}

const config = buildConfig(import.meta.env);
const textEncoder = new TextEncoder();

function generateRequestId(): string {
  const h = '0123456789abcdef';
  const seg = [8, 4, 4, 4, 12];
  return seg.map((len) => Array.from({ length: len }, () => h[Math.floor(Math.random() * 16)]).join('')).join('-');
}

function goQueryEscape(s: string): string {
  return encodeURIComponent(s)
    .replace(/%20/g, '+')
    .replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}

function canonicalQuery(params: URLSearchParams): string {
  const grouped = new Map<string, string[]>();
  params.forEach((value, key) => {
    const values = grouped.get(key);
    if (values) {
      values.push(value);
    } else {
      grouped.set(key, [value]);
    }
  });
  const pairs: string[] = [];
  Array.from(grouped.keys()).sort().forEach((key) => {
    (grouped.get(key) ?? []).slice().sort().forEach((value) => {
      pairs.push(`${goQueryEscape(key)}=${goQueryEscape(value)}`);
    });
  });
  return pairs.join('&');
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', data as unknown as BufferSource);
  return Array.from(new Uint8Array(buffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function hmacSha256Hex(key: string, data: string): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey('raw', textEncoder.encode(key), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, textEncoder.encode(data));
  return Array.from(new Uint8Array(signature)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function signedHeaders(method: string, path: string, bodyBytes: Uint8Array, token: string | null): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'X-Request-Id': generateRequestId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (!config.requestSecurityEnabled) {
    return headers;
  }
  const secret = token ?? config.requestSecurityAnonymousSecret;
  if (!secret) {
    throw new Error('missing_request_signature_secret');
  }
  const timestamp = Math.round(Date.now());
  const nonce = generateRequestId();
  const bodyHash = await sha256Hex(bodyBytes);
  const url = new URL(path, 'http://localhost');
  const canonical = [
    method.toUpperCase(),
    url.pathname || '/',
    canonicalQuery(url.searchParams),
    bodyHash,
    String(timestamp),
    nonce,
  ].join('\n');
  headers['X-Req-Timestamp'] = String(timestamp);
  headers['X-Req-Nonce'] = nonce;
  headers['X-Req-Body-Hash'] = bodyHash;
  headers['X-Req-Signature'] = await hmacSha256Hex(secret, canonical);
  return headers;
}

function encodeMultipart(file: File, boundary: string, fileBytes: Uint8Array): Uint8Array {
  const safeFilename = file.name.replace(/[\r\n"]/g, '_');
  const contentType = file.type || 'application/octet-stream';
  const head = textEncoder.encode(
    `--${boundary}\r\n` +
    `Content-Disposition: form-data; name="file"; filename="${safeFilename}"\r\n` +
    `Content-Type: ${contentType}\r\n\r\n`,
  );
  const tail = textEncoder.encode(`\r\n--${boundary}--\r\n`);
  const body = new Uint8Array(head.length + fileBytes.length + tail.length);
  body.set(head, 0);
  body.set(fileBytes, head.length);
  body.set(tail, head.length + fileBytes.length);
  return body;
}

async function parseResponse<T>(res: Response, path: string): Promise<T> {
  const serverTime = res.headers.get('X-Server-Time');
  if (serverTime) {
    const ts = Number(serverTime);
    if (!Number.isNaN(ts)) updateServerTimeOffset(ts);
  }
  const raw = await res.text();
  const data = raw.trim() ? JSON.parse(raw) as Record<string, unknown> : {};
  if (!res.ok) {
    if (res.status === 401) handleUnauthorized();
    const errorValue = data.error;
    const code = typeof errorValue === 'string'
      ? errorValue
      : typeof errorValue === 'object' && errorValue && 'code' in errorValue && typeof errorValue.code === 'string'
        ? errorValue.code
        : 'unknown_error';
    throw { status: res.status, message: code, path };
  }
  return data as T;
}

async function multipartPost<T>(path: string, file: File, idempotencyKey: string): Promise<T> {
  const token = getAccessToken();
  const boundary = `----aiptyg-admin-${generateRequestId()}`;
  const fileBytes = new Uint8Array(await file.arrayBuffer());
  const bodyBytes = encodeMultipart(file, boundary, fileBytes);
  const headers = await signedHeaders('POST', path, bodyBytes, token);
  headers['Content-Type'] = `multipart/form-data; boundary=${boundary}`;
  headers['Idempotency-Key'] = idempotencyKey;
  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}${path}`, {
      method: 'POST',
      headers,
      body: bodyBytes as unknown as BodyInit,
    });
  } catch {
    throw { status: 0, message: 'network_error', path };
  }
  return parseResponse<T>(res, path);
}

export function previewMessageImport(taskId: number, file: File, idempotencyKey: string): Promise<MessageImportBatchDTO> {
  return multipartPost<MessageImportBatchDTO>(`/v1/admin/tasks/${taskId}/message-imports/preview`, file, idempotencyKey);
}

export function commitMessageImport(taskId: number, batchId: string): Promise<MessageImportBatchDTO> {
  return post<MessageImportBatchDTO>(`/v1/admin/tasks/${taskId}/message-imports/${batchId}/commit`, { batch_id: Number(batchId) || batchId });
}

export function getMessageImportBatch(taskId: number, batchId: string): Promise<MessageImportBatchDTO> {
  return get<MessageImportBatchDTO>(`/v1/admin/tasks/${taskId}/message-imports/${batchId}`);
}

export function getMessagePoolStats(taskId: number): Promise<MessagePoolStatsDTO> {
  return get<MessagePoolStatsDTO>(`/v1/admin/tasks/${taskId}/message-pool/stats`);
}

function filenameFromContentDisposition(value: string | null, fallback: string): string {
  const match = value?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function exportMessageUnsentItems(taskId: number): Promise<void> {
  const path = `/v1/admin/tasks/${taskId}/message-items/unsent-export`;
  const token = getAccessToken();
  const headers = await signedHeaders('GET', path, new Uint8Array(), token);
  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}${path}`, {
      method: 'GET',
      headers,
    });
  } catch {
    throw { status: 0, message: 'network_error', path };
  }
  if (!res.ok) {
    await parseResponse<never>(res, path);
    return;
  }
  const filename = filenameFromContentDisposition(
    res.headers.get('Content-Disposition'),
    `message-unsent-${taskId}.csv`,
  );
  downloadBlob(filename, await res.blob());
}

export function suppressMessageItem(taskId: number, itemId: string, reason: string): Promise<MessagePoolStatsDTO> {
  return post<MessagePoolStatsDTO>(`/v1/admin/tasks/${taskId}/message-items/${itemId}/suppress`, { reason });
}

export function setMessageKillSwitch(enabled: boolean, auditReason: string): Promise<{ enabled: boolean }> {
  return post<{ enabled: boolean }>('/v1/admin/message-kill-switch', { enabled, audit_reason: auditReason });
}
